import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenant, useUpdateTenant } from '../../api/tenants.js';
import { useTenantMembers, useUserByUid } from '../../api/users.js';
import { ApiError } from '../../api/client.js';
import type { Membership } from '../../api/types.js';
import { ReminderCadencePicker } from './ReminderCadencePicker.js';
import { PageIdentity, tenantSlug } from '../../shell/PageIdentity.js';
import { PageWrapper } from '../../shell/PageWrapper.js';

// Roles eligible for "default comptable". REQ wording said
// ap_clerk / finance_lead; the contract enum uses ap_clerk / ap_admin so
// we map finance_lead → ap_admin here.
const ELIGIBLE_ROLES: Membership['role'][] = ['ap_clerk', 'ap_admin'];
// Roles that can WRITE tenant settings (contract: admin scope).
const WRITE_ROLES: Membership['role'][] = ['ap_admin', 'group_admin'];

interface Props {
  tenantId: string;
  tenantName: string;
  callerRole: Membership['role'] | null;
}

export function TenantSettingsScreen({ tenantId, tenantName, callerRole }: Props) {
  const tenantQuery = useTenant(tenantId);
  const members = useTenantMembers(tenantId);
  const updateMutation = useUpdateTenant(tenantId);
  const [draftUid, setDraftUid] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const tenant = tenantQuery.data;
  const canWrite = callerRole !== null && WRITE_ROLES.includes(callerRole);

  // Seed the draft with the current value when the tenant loads.
  useEffect(() => {
    if (tenant) setDraftUid(tenant.defaultComptableUid ?? null);
  }, [tenant]);

  const allMembers = members.data ?? [];
  const eligibleMembers = allMembers.filter((m) => ELIGIBLE_ROLES.includes(m.role));
  const pickerMembers = eligibleMembers.length > 0 ? eligibleMembers : allMembers;

  const dirty = (tenant?.defaultComptableUid ?? null) !== draftUid;

  async function save() {
    setToast(null);
    try {
      await updateMutation.mutateAsync({ defaultComptableUid: draftUid });
      setToast('Comptable par défaut enregistré.');
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setToast('Vous n’avez pas les droits pour modifier ce paramètre.');
      } else {
        setToast('Échec de l’enregistrement.');
      }
    }
  }

  return (
    <PageWrapper>
      <section className="settings" aria-label="Paramètres du tenant">
        <PageIdentity
          testId="settings"
          eyebrow={tenantSlug(tenantName)}
          title="Paramètres"
          subtitle={
            <>
              Configuration spécifique à <strong>{tenantName}</strong>. Pour la configuration
              globale du groupe (PDP, notifications, audit), voir Administration → Paramètres.
            </>
          }
        />

        {!canWrite ? (
          <div className="invoices__error" role="status" data-testid="settings-readonly-banner">
            <span>
              Vous êtes en lecture seule sur ce tenant. Seuls les rôles ap_admin / group_admin
              peuvent modifier ces paramètres.
            </span>
          </div>
        ) : null}

        <article className="invoice-detail__card" data-testid="settings-identity">
          <div className="eyebrow">Identité du tenant</div>
          {tenantQuery.isLoading ? (
            <p className="muted">Chargement…</p>
          ) : tenantQuery.isError || !tenant ? (
            <p className="invoices__error" role="alert">
              Impossible de charger le tenant.
            </p>
          ) : (
            <dl className="invoice-detail__facts">
              <div>
                <dt>Raison sociale</dt>
                <dd data-testid="settings-tenant-name">{tenant.legalName}</dd>
              </div>
              <div>
                <dt>SIREN</dt>
                <dd data-testid="settings-tenant-siren">{tenant.siren ?? '—'}</dd>
              </div>
              <div>
                <dt>Forme juridique</dt>
                <dd>{tenant.legalForm ?? '—'}</dd>
              </div>
            </dl>
          )}
        </article>

        <article className="invoice-detail__card">
          <div className="eyebrow">Comptable par défaut</div>
          <p className="muted" style={{ marginTop: 0 }}>
            Comptable assigné par défaut aux factures entrantes de ce tenant.
          </p>
          {tenant ? <CurrentComptable uid={tenant.defaultComptableUid ?? null} /> : null}
          <label className="dispute-form__label" htmlFor="settings-comptable-picker">
            <span className="eyebrow">Choisir un membre</span>
            <select
              id="settings-comptable-picker"
              className="modal__search"
              value={draftUid ?? ''}
              onChange={(e) => setDraftUid(e.target.value === '' ? null : e.target.value)}
              disabled={!canWrite || members.isLoading}
              data-testid="settings-comptable-picker"
            >
              <option value="">— Aucun comptable par défaut —</option>
              {pickerMembers.map((m) => (
                <PickerOption key={m.uid} membership={m} />
              ))}
            </select>
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn-primary"
              onClick={save}
              disabled={!canWrite || !dirty || updateMutation.isPending}
              data-testid="settings-save"
            >
              {updateMutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
          {toast ? (
            <div className="invoice-detail__toast" role="status" data-testid="settings-toast">
              {toast}
            </div>
          ) : null}
        </article>

        <ReminderCadencePicker />

        <article className="invoice-detail__card" data-testid="settings-members-preview">
          <div className="eyebrow">Membres</div>
          <p>
            <strong>{allMembers.length}</strong> membre{allMembers.length > 1 ? 's' : ''} sur ce
            tenant.
          </p>
          <Link
            to={`/admin/members?tenantId=${encodeURIComponent(tenantId)}`}
            className="btn-ghost"
            data-testid="settings-members-link"
          >
            → Gérer les membres
          </Link>
        </article>
      </section>
    </PageWrapper>
  );
}

function CurrentComptable({ uid }: { uid: string | null }) {
  if (!uid) {
    return (
      <p className="muted" data-testid="settings-current-comptable">
        Aucun comptable par défaut.
      </p>
    );
  }
  return <CurrentComptableResolver uid={uid} />;
}

function CurrentComptableResolver({ uid }: { uid: string }) {
  const lookup = useUserByUid(uid);
  const user = lookup.data ?? null;
  return (
    <p data-testid="settings-current-comptable">
      <strong>{user?.displayName ?? user?.email ?? uid}</strong>
      {user?.email && user.displayName ? <span className="muted"> · {user.email}</span> : null}
    </p>
  );
}

function PickerOption({ membership }: { membership: Membership }) {
  const lookup = useUserByUid(membership.uid);
  const user = lookup.data ?? null;
  const label = user?.displayName ?? user?.email ?? membership.uid;
  return (
    <option value={membership.uid} data-testid={`settings-comptable-option-${membership.uid}`}>
      {label} — {membership.role}
    </option>
  );
}
